import {
  BoxProps,
  Button,
  ChakraSelect,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useDisclosure,
  VStack,
} from '@elkaso-app/web-design';
import { useUploadFileApi } from 'apis/config/use-upload-file-api';
import { useCreateRestaurantApi } from 'apis/restaurants/use-create-restaurant-api';
import { useGetCountriesApi } from 'apis/users/use-get-countries-api';
import { MapPicker } from 'pages/restaurants/components/create-restaurant/map-picker';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import PhoneInput from 'react-phone-number-input';

type TCountryOption = {
  value: string;
  label: string;
};

type TFormValues = {
  nameEn: string;
  nameAr: string;
  countryCode: TCountryOption | null;
  imageUrl: string;
  branchNameEn: string;
  branchNameAr: string;
  branchFullLine: string;
  adminFullName: string;
  adminPhoneNumber: string;
  adminEmail: string;
};

const schema = yup.object().shape({
  nameEn: yup.string().required('Restaurant name (English) is required'),
  nameAr: yup
    .string()
    .matches(/^$|^[\u0600-\u06FF\s0-9\u0660-\u0669\u06F0-\u06F9]+$/, 'Please enter Arabic letters only')
    .nullable(),
  countryCode: yup
    .object()
    .shape({
      value: yup.string().required(),
      label: yup.string().required(),
    })
    .nullable()
    .required('Country is required'),
  imageUrl: yup.string().url('Must be a valid URL').nullable(),
  branchNameEn: yup.string().required('Branch name (English) is required'),
  branchNameAr: yup
    .string()
    .matches(/^$|^[\u0600-\u06FF\s0-9\u0660-\u0669\u06F0-\u06F9]+$/, 'Please enter Arabic letters only')
    .nullable(),
  branchFullLine: yup.string().required('Branch address is required'),
  adminFullName: yup.string().required('Admin name is required'),
  adminPhoneNumber: yup.string().required('Phone number is required'),
  adminEmail: yup
    .string()
    .matches(/^$|^[\w-+.]+@([\w-]+\.)+[\w-]{2,4}$/, 'Invalid email format')
    .nullable(),
});

const defaultValues: TFormValues = {
  nameEn: '',
  nameAr: '',
  countryCode: null,
  imageUrl: '',
  branchNameEn: '',
  branchNameAr: '',
  branchFullLine: '',
  adminFullName: '',
  adminPhoneNumber: '',
  adminEmail: '',
};

export interface ICreateRestaurantModalProps extends BoxProps {
  trigger: JSX.Element;
}

export const CreateRestaurantModal = ({ trigger }: ICreateRestaurantModalProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isLoading: isCountriesLoading, data: countries = [] } = useGetCountriesApi();
  const { isLoading: isCreateLoading, mutate: createRestaurant } = useCreateRestaurantApi();
  const { mutate: uploadFile, isLoading: isUploadLoading } = useUploadFileApi();

  const [coordinates, setCoordinates] = useState<{ lat: number; lng: number } | null>(null);

  const countryOptions: TCountryOption[] = countries.map((country: any) => ({
    value: country.code,
    label: country.name,
  }));

  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<TFormValues>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(schema) as any,
    defaultValues: { ...defaultValues },
  });

  const onSubmit = (data: TFormValues) => {
    const body: any = {
      nameEn: data.nameEn,
      countryCode: data.countryCode?.value,
      branch: {
        nameEn: data.branchNameEn,
        fullLine: data.branchFullLine,
      },
      admin: {
        fullName: data.adminFullName,
        phoneNumber: data.adminPhoneNumber,
      },
    };

    if (data.nameAr) body.nameAr = data.nameAr;
    if (data.imageUrl) body.imageUrl = data.imageUrl;
    if (data.branchNameAr) body.branch.nameAr = data.branchNameAr;
    if (coordinates) {
      body.branch.coordinates = {
        latitude: coordinates.lat,
        longitude: coordinates.lng,
      };
    }
    if (data.adminEmail) body.admin.email = data.adminEmail;

    createRestaurant(
      { body },
      {
        onSuccess: () => {
          onCloseModal();
        },
      }
    );
  };

  const onCloseModal = () => {
    onClose();
    reset(defaultValues);
    setCoordinates(null);
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    uploadFile(
      { body: formData },
      {
        onSuccess: (response) => {
          setValue('imageUrl', response.url, { shouldValidate: true });
        },
      }
    );
  };

  return (
    <>
      {React.cloneElement(trigger, { onClick: onOpen })}

      <Modal isOpen={isOpen} onClose={onCloseModal} closeOnOverlayClick={false} size='5xl'>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <VStack align='start' spacing='xs'>
              <Heading as='h2' size='lg'>
                Create Restaurant
              </Heading>
              <Text color='gray.500' fontWeight='normal' fontSize='md'>
                Create a new restaurant with its first branch and admin user
              </Text>
            </VStack>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <Stack as='form' id='createRestaurantForm' spacing='xl' onSubmit={handleSubmit(onSubmit)} noValidate>
              {/* Section 1: Restaurant Details */}
              <VStack align='stretch' spacing='md'>
                <Heading as='h3' size='md' color='gray.700'>
                  Restaurant Details
                </Heading>

                <HStack align='start' spacing='md'>
                  <FormControl isRequired isInvalid={!!errors.nameEn} flex={1}>
                    <FormLabel>Name (English)</FormLabel>
                    <Input placeholder='e.g. Downtown Kitchen' {...register('nameEn')} />
                    <FormErrorMessage>{errors.nameEn?.message}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors.nameAr} flex={1}>
                    <FormLabel>Name (Arabic)</FormLabel>
                    <Input
                      placeholder='Defaults to English name'
                      dir='rtl'
                      textAlign='start'
                      {...register('nameAr')}
                    />
                    <FormErrorMessage>{errors.nameAr?.message}</FormErrorMessage>
                  </FormControl>
                </HStack>

                <HStack align='start' spacing='md'>
                  <Controller
                    control={control}
                    name='countryCode'
                    render={({ field: { onChange, onBlur, value, name, ref }, fieldState: { error, invalid } }) => (
                      <FormControl isRequired isInvalid={invalid} flex={1}>
                        <FormLabel>Country</FormLabel>
                        <ChakraSelect
                          placeholder='Select country'
                          options={countryOptions}
                          id='countryCode'
                          onChange={onChange}
                          onBlur={onBlur}
                          value={value}
                          name={name}
                          ref={ref}
                          isLoading={isCountriesLoading}
                        />
                        <FormErrorMessage>{error?.message}</FormErrorMessage>
                      </FormControl>
                    )}
                  />

                  <FormControl isInvalid={!!errors.imageUrl} flex={1}>
                    <FormLabel>Restaurant Logo</FormLabel>
                    <Input
                      type='file'
                      accept='image/jpeg,image/png,image/svg+xml'
                      onChange={handleImageUpload}
                      sx={{ pt: '4px' }}
                    />
                    {isUploadLoading && (
                      <Text fontSize='xs' color='gray.500'>
                        Uploading...
                      </Text>
                    )}
                    <FormErrorMessage>{errors.imageUrl?.message}</FormErrorMessage>
                  </FormControl>
                </HStack>
              </VStack>

              {/* Section 2: First Branch */}
              <VStack align='stretch' spacing='md'>
                <Heading as='h3' size='md' color='gray.700'>
                  First Branch
                </Heading>

                <HStack align='start' spacing='md'>
                  <FormControl isRequired isInvalid={!!errors.branchNameEn} flex={1}>
                    <FormLabel>Branch Name (English)</FormLabel>
                    <Input placeholder='e.g. Main Branch' {...register('branchNameEn')} />
                    <FormErrorMessage>{errors.branchNameEn?.message}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors.branchNameAr} flex={1}>
                    <FormLabel>Branch Name (Arabic)</FormLabel>
                    <Input placeholder='Optional' dir='rtl' textAlign='start' {...register('branchNameAr')} />
                    <FormErrorMessage>{errors.branchNameAr?.message}</FormErrorMessage>
                  </FormControl>
                </HStack>

                <FormControl isRequired isInvalid={!!errors.branchFullLine}>
                  <FormLabel>Address</FormLabel>
                  <Input placeholder='e.g. 123 Main Street, Riyadh, SA' {...register('branchFullLine')} />
                  <FormErrorMessage>{errors.branchFullLine?.message}</FormErrorMessage>
                </FormControl>

                <FormControl>
                  <FormLabel>Location (click map or drag pin)</FormLabel>
                  <MapPicker
                    lat={coordinates?.lat ?? null}
                    lng={coordinates?.lng ?? null}
                    onCoordinatesChange={(lat, lng) => setCoordinates({ lat, lng })}
                    height='250px'
                  />
                </FormControl>
              </VStack>

              {/* Section 3: Restaurant Admin */}
              <VStack align='stretch' spacing='md'>
                <Heading as='h3' size='md' color='gray.700'>
                  Restaurant Admin
                </Heading>

                <FormControl isRequired isInvalid={!!errors.adminFullName}>
                  <FormLabel>Full Name</FormLabel>
                  <Input placeholder='e.g. Ahmed Al-Saud' {...register('adminFullName')} />
                  <FormErrorMessage>{errors.adminFullName?.message}</FormErrorMessage>
                </FormControl>

                <HStack align='start' spacing='md'>
                  <Controller
                    control={control}
                    name='adminPhoneNumber'
                    rules={{
                      required: 'Phone number is required',
                    }}
                    render={({ field: { onChange, onBlur, value, name, ref }, fieldState: { error, invalid } }) => (
                      <FormControl isRequired isInvalid={invalid} flex={1}>
                        <FormLabel>Phone Number</FormLabel>
                        <PhoneInput
                          international
                          placeholder='Enter phone number'
                          defaultCountry='AE'
                          value={value}
                          onChange={onChange}
                          onBlur={onBlur}
                          name={name}
                          ref={ref}
                          inputComponent={Input}
                        />
                        <FormErrorMessage>{error?.message}</FormErrorMessage>
                      </FormControl>
                    )}
                  />

                  <FormControl isInvalid={!!errors.adminEmail} flex={1}>
                    <FormLabel>Email</FormLabel>
                    <Input type='email' placeholder='admin@restaurant.com' {...register('adminEmail')} />
                    <FormErrorMessage>{errors.adminEmail?.message}</FormErrorMessage>
                  </FormControl>
                </HStack>
              </VStack>
            </Stack>
          </ModalBody>

          <ModalFooter>
            <HStack spacing='md'>
              <Button variant='ghost' colorScheme='gray' onClick={onCloseModal}>
                Cancel
              </Button>
              <Button
                variant='solid'
                colorScheme='red'
                type='submit'
                form='createRestaurantForm'
                isLoading={isCreateLoading}
                isDisabled={isUploadLoading}>
                Create Restaurant
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
